/**
 * Seção da família de qualidade (requisito A14 da PoC), consumindo o contrato
 * congelado de `GET /atendimentos/indicadores/qualidade`.
 *
 * O que esta seção compartilha com a família operacional: a rota, a permissão,
 * o menu, o filtro (`FiltroIndicadores`/`QueryRequestProvider`) e a moldura.
 * O que é próprio dela: os `_requests`, o response provider (chave de cache
 * separada), os cards e a rota da exportação — a exportação segue a aba exibida,
 * porque baixar o arquivo da outra família seria evidência errada.
 *
 * Regra estrutural: os quatro blocos são irmãos e a suspeita de interrupção
 * nunca soma, nem se aninha, ao indicador oficial de consultas interrompidas.
 */
import clsx from 'clsx'
import { formatDateToPtBr } from '../../../../../../../_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { InfoAlert, ListLoading } from '../../../../../../components'
import { useParametrosIndicadores } from '../../core/QueryRequestProvider'
import { useQueryResponseQualidade } from '../../core/QueryResponseQualidadeProvider'
import { CardSatisfacao } from '../qualidade/CardSatisfacao'
import { CardSuspeita } from '../qualidade/CardSuspeita'
import { CardTaxaQualidade } from '../qualidade/CardTaxaQualidade'
import { TabelaQuebra } from '../qualidade/TabelaQuebra'

export const SecaoQualidade = () => {
  const { t } = useT()
  const { parametros } = useParametrosIndicadores()
  const { singleResponse: dados, isLoading } = useQueryResponseQualidade()

  if (!parametros) {
    return (
      <InfoAlert variant='info' className='mb-0'>
        {t('indicadores.estado.selecione_empresa')}
      </InfoAlert>
    )
  }

  return (
    <div aria-live='polite' aria-busy={isLoading}>
      {isLoading && <ListLoading />}
      {isLoading && !dados && (
        <span className='visually-hidden'>{t('indicadores.estado.carregando')}</span>
      )}

      {dados && (
        <div className={clsx(isLoading && 'opacity-50')}>
          <p className='fs-7 text-gray-700 mb-5'>
            {t('indicadores.periodo', {
              inicio: formatDateToPtBr(dados.dataInicio),
              fim: formatDateToPtBr(dados.dataFim),
            })}
          </p>

          <section className='mb-8' aria-labelledby='qualidade-satisfacao'>
            <h3 id='qualidade-satisfacao' className='fs-5 fw-bold text-gray-900 mb-4'>
              {t('indicadores.qualidade.satisfacao.titulo')}
            </h3>

            <div className='row g-5'>
              <div className='col-xl-3 col-md-6'>
                <CardTaxaQualidade
                  titulo={t('indicadores.qualidade.satisfacao.taxa_resposta')}
                  valor={dados.satisfacao.taxaResposta}
                  numerador={{
                    rotulo: t('indicadores.qualidade.satisfacao.respondidas'),
                    valor: dados.satisfacao.respondidas,
                  }}
                  denominador={{
                    rotulo: t('indicadores.qualidade.satisfacao.elegiveis'),
                    valor: dados.satisfacao.elegiveis,
                  }}
                  extras={[
                    {
                      rotulo: t('indicadores.qualidade.satisfacao.fora_do_elegivel'),
                      valor: dados.satisfacao.respondidasForaDoElegivel,
                    },
                  ]}
                  notas={[dados.rotulos?.elegivelSatisfacao]}
                />
              </div>
              <div className='col-xl-3 col-md-6'>
                <CardSatisfacao
                  titulo={t('indicadores.qualidade.satisfacao.dimensao.atendimento')}
                  dimensao={dados.satisfacao.atendimento}
                  notaSupressao={dados.rotulos?.supressaoNominal}
                  notaNaoRespondido={dados.rotulos?.zeroNaoRespondido}
                />
              </div>
              <div className='col-xl-3 col-md-6'>
                <CardSatisfacao
                  titulo={t('indicadores.qualidade.satisfacao.dimensao.telekit')}
                  dimensao={dados.satisfacao.telekit}
                  notaSupressao={dados.rotulos?.supressaoNominal}
                  notaNaoRespondido={dados.rotulos?.zeroNaoRespondido}
                />
              </div>
              <div className='col-xl-3 col-md-6'>
                <CardSatisfacao
                  titulo={t('indicadores.qualidade.satisfacao.dimensao.estabelecimento')}
                  dimensao={dados.satisfacao.estabelecimento}
                  notaSupressao={dados.rotulos?.supressaoNominal}
                  notaNaoRespondido={dados.rotulos?.zeroNaoRespondido}
                />
              </div>
            </div>
          </section>

          <section className='mb-8' aria-labelledby='qualidade-interrompidas'>
            <h3 id='qualidade-interrompidas' className='fs-5 fw-bold text-gray-900 mb-4'>
              {t('indicadores.qualidade.interrompidas.titulo')}
            </h3>

            <CardTaxaQualidade
              titulo={t('indicadores.qualidade.interrompidas.taxa')}
              valor={dados.interrompidas.taxa}
              numerador={{
                rotulo: t('indicadores.qualidade.interrompidas.declaradas'),
                valor: dados.interrompidas.atendimentosComInterrupcaoDeclarada,
              }}
              denominador={{
                rotulo: t('indicadores.qualidade.interrompidas.presenca_paciente'),
                valor: dados.interrompidas.atendimentosComPresencaPaciente,
              }}
              extras={[
                {
                  rotulo: t('indicadores.qualidade.interrompidas.fora_do_denominador'),
                  valor: dados.interrompidas.declaradasForaDoDenominador,
                },
                {
                  rotulo: t('indicadores.qualidade.interrompidas.retomadas'),
                  valor: dados.interrompidas.retomadas,
                },
              ]}
              notas={[dados.rotulos?.denominadorInterrompidas, dados.rotulos?.pisoDeclaracao]}
            >
              <TabelaQuebra
                titulo={t('indicadores.qualidade.interrompidas.por_causa')}
                colunaCategoria={t('indicadores.qualidade.interrompidas.coluna_categoria')}
                colunaValor={t('indicadores.qualidade.interrompidas.coluna_valor')}
                dados={dados.interrompidas.porCausa}
                textoVazio={t('indicadores.qualidade.interrompidas.vazio')}
                prefixoRotulo='indicadores.qualidade.causa'
              />
              <TabelaQuebra
                titulo={t('indicadores.qualidade.interrompidas.por_quem_caiu')}
                colunaCategoria={t('indicadores.qualidade.interrompidas.coluna_categoria')}
                colunaValor={t('indicadores.qualidade.interrompidas.coluna_valor')}
                dados={dados.interrompidas.porQuemCaiu}
                textoVazio={t('indicadores.qualidade.interrompidas.vazio')}
                prefixoRotulo='indicadores.qualidade.quem_caiu'
              />
            </CardTaxaQualidade>
          </section>

          <section className='mb-8' aria-labelledby='qualidade-suspeita'>
            <h3 id='qualidade-suspeita' className='fs-5 fw-bold text-gray-900 mb-4'>
              {t('indicadores.qualidade.suspeita.titulo')}
            </h3>

            <CardSuspeita
              dados={dados.suspeitaInterrupcao}
              notaNaoSomada={dados.rotulos?.suspeitaNaoSomada}
              notaCobertura={dados.rotulos?.coberturaSuspeita}
            />
          </section>

          <section aria-labelledby='qualidade-nao-realizadas'>
            <h3 id='qualidade-nao-realizadas' className='fs-5 fw-bold text-gray-900 mb-4'>
              {t('indicadores.qualidade.nao_realizadas.titulo')}
            </h3>

            <CardTaxaQualidade
              titulo={t('indicadores.qualidade.nao_realizadas.taxa')}
              valor={dados.naoRealizadas.taxa}
              numerador={{
                rotulo: t('indicadores.qualidade.nao_realizadas.cancelados_faltas'),
                valor: dados.naoRealizadas.canceladosEFaltas,
              }}
              denominador={{
                rotulo: t('indicadores.qualidade.nao_realizadas.agendados'),
                valor: dados.naoRealizadas.agendadosNoPeriodo,
              }}
              extras={[
                {
                  rotulo: t('indicadores.qualidade.nao_realizadas.sem_motivo_tipado'),
                  valor: dados.naoRealizadas.semMotivoTipado,
                },
              ]}
            >
              <TabelaQuebra
                titulo={t('indicadores.qualidade.nao_realizadas.por_classe')}
                colunaCategoria={t('indicadores.qualidade.nao_realizadas.coluna_categoria')}
                colunaValor={t('indicadores.qualidade.nao_realizadas.coluna_valor')}
                dados={dados.naoRealizadas.porClasse}
                textoVazio={t('indicadores.qualidade.nao_realizadas.vazio')}
                prefixoRotulo='indicadores.qualidade.classe'
              />
              <TabelaQuebra
                titulo={t('indicadores.qualidade.nao_realizadas.por_codigo')}
                colunaCategoria={t('indicadores.qualidade.nao_realizadas.coluna_categoria')}
                colunaValor={t('indicadores.qualidade.nao_realizadas.coluna_valor')}
                dados={dados.naoRealizadas.porCodigo}
                textoVazio={t('indicadores.qualidade.nao_realizadas.vazio')}
                prefixoRotulo='indicadores.qualidade.codigo'
              />
              <TabelaQuebra
                titulo={t('indicadores.qualidade.nao_realizadas.por_registro')}
                colunaCategoria={t('indicadores.qualidade.nao_realizadas.coluna_categoria')}
                colunaValor={t('indicadores.qualidade.nao_realizadas.coluna_valor')}
                dados={dados.naoRealizadas.porRegistro}
                textoVazio={t('indicadores.qualidade.nao_realizadas.vazio')}
                prefixoRotulo='indicadores.qualidade.registro'
              />
              <TabelaQuebra
                titulo={t('indicadores.qualidade.nao_realizadas.por_antecedencia')}
                colunaCategoria={t('indicadores.qualidade.nao_realizadas.coluna_categoria')}
                colunaValor={t('indicadores.qualidade.nao_realizadas.coluna_valor')}
                dados={dados.naoRealizadas.porAntecedencia}
                textoVazio={t('indicadores.qualidade.nao_realizadas.vazio')}
                prefixoRotulo='indicadores.qualidade.antecedencia'
                valoresRotulo={{ horas: dados.naoRealizadas.horasCancelamentoTardio }}
                nota={dados.rotulos?.antecedenciaNaoCalculavel}
              />
            </CardTaxaQualidade>
          </section>
        </div>
      )}
    </div>
  )
}
